import './createInviteLink.scss'
import { useState, useCallback } from 'react'
import { Button, Input, LoadingIndicator } from '@/shared/ui'
import { SpecialInput } from '../special-input'
import {
    InviteLinkWithAnalyticsType,
    useGenerateInviteLinkMutation,
} from '@/shared/api'
import { useGenerateLinkValidation, generateLinkSchemaChannels } from '../hooks'
import { InviteSVGIcon, PlusSVGIcon } from '@/shared/assets'
import { useParams } from 'react-router'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account/hooks'
import { useActiveUser, Channel } from '@/true-fsd/data/advertiser-account'

interface GenerateLinkData {
    linkName: string
    budget: number
    channelName: string | null
    title: string | null
}

interface CreateInviteLinkProps {
    activeChannel: Channel

    onInviteLinkCreated?: (newLink: InviteLinkWithAnalyticsType) => void
    onInviteLinkUpdated?: (updatedLink: InviteLinkWithAnalyticsType) => void
    onRemoveInviteLink?: (newLink: InviteLinkWithAnalyticsType) => void
}

export const CreateChannelInviteLink = ({
    activeChannel,
    onInviteLinkCreated,
    onInviteLinkUpdated,
    onRemoveInviteLink,
}: CreateInviteLinkProps) => {
    const { groupId } = useParams<{ groupId: string }>()

    const user = useActiveUser()
    const { subscription } = useActiveWorkspace()

    const { isValid, errors, validate } =
        useGenerateLinkValidation<GenerateLinkData>(generateLinkSchemaChannels)
    const generateInviteLink = useGenerateInviteLinkMutation()

    const [generateLinkData, setGenerateLinkData] = useState<GenerateLinkData>({
        linkName: '',
        budget: 0,
        channelName: null,
        title: '',
    })

    const setLinkName = useCallback((value: string) => {
        setGenerateLinkData((prev) => ({ ...prev, linkName: value }))
    }, [])

    const setLinkBudget = useCallback((value: number) => {
        setGenerateLinkData((prev) => ({ ...prev, budget: value }))
    }, [])

    const setChannelName = useCallback((name: string | null) => {
        setGenerateLinkData((prev) => ({ ...prev, channelName: name }))
    }, [])

    // const setChannelTitle = useCallback((title: string | null) => {
    //     setGenerateLinkData((prev) => ({ ...prev, title: title }))
    // }, [])

    const handleBudgetChange = useCallback(
        (value: string) => {
            const budget = parseFloat(value)
            setLinkBudget(isNaN(budget) ? 0 : budget)
        },
        [setLinkBudget]
    )

    const [isCreating, setIsCreating] = useState(false)
    const generateLinkHandler = useCallback(async () => {
        if (!activeChannel || !groupId) return

        const isValid = validate(generateLinkData)
        if (!isValid) {
            return
        }

        try {
            const { linkName, budget, channelName } = generateLinkData

            // Optimistic UI: Create a temporary invite link object
            const tempInviteLink: InviteLinkWithAnalyticsType = {
                budget,
                status: 'no_status', // Status to indicate pending creation
                invite_link: '', // Initially null, updated later
                created_at: new Date(),
                user_id: user.user_id, // Add missing property
                channel_id: activeChannel.channel_id, // Add missing property
                invite_link_id: 0, // Add missing property (can be set later)
                invite_link_name: (linkName || channelName) ?? '',
                invite_links_group_id: 0,
                total_subscribers: 0,
                subscribed_count: 0,
                subscribed_count_per_day: 0,
                unsubscribed_count: 0,
                total_premium_subscribers: 0,
                premium_subscribed_count: 0,
                premium_unsubscribed_count: 0,
                ctr: null,
                cpm: null,
                cps: null,
                total_cps: null,
                unsubscribed_percentage: null,
                premium_unsubscribed_percentage: null,
                publishing_duration: null,
                published_at: null,
                updated_at: new Date(),
                deleted_at: null,
                external_channel_id: null,
                active_creative: null,
                active_creative_id: null,
                analytics: [],
                publisher: null,
                external_channel: null,
                subscribers: [],
            }

            // Notify parent component (InviteLinksWidget) to add this link to the table
            if (onInviteLinkCreated) {
                onInviteLinkCreated(tempInviteLink)
            }

            setIsCreating(true)
            generateInviteLink.mutate(
                {
                    channelId: activeChannel.channel_id,
                    userId: activeChannel.user_id,
                    name: (linkName || channelName) ?? '',
                    groupId: parseInt(groupId),
                    budget,
                    creatorUserId: user.user_id,
                },
                {
                    onSettled: (data, error) => {
                        setIsCreating(false)
                        if (error) {
                            console.error(error)
                        }
                        if (data) {
                            // Update the temporary invite link with the actual URL from the backend
                            // Similarly for onInviteLinkUpdated
                            if (onInviteLinkUpdated) {
                                onInviteLinkUpdated({ ...data }) //
                            }
                        }
                    },
                    onError: (data) => {
                        if (onRemoveInviteLink) {
                            onRemoveInviteLink(tempInviteLink)
                        }

                        toast.error(
                            (
                                (data as AxiosError).response?.data as Record<
                                    string,
                                    string
                                >
                            )?.message
                        )
                    },
                }
            )
        } catch (error) {
            console.error('Failed to generate invite link:', error)
        }
        setGenerateLinkData({
            linkName: '',
            budget: 0,
            channelName: null,
            title: null,
        })
    }, [
        activeChannel,
        validate,
        generateLinkData,
        generateInviteLink,
        groupId,
        user,
        onInviteLinkCreated,
        onInviteLinkUpdated,
        onRemoveInviteLink,
    ])

    const forbidToCreateGroups = subscription.status === 'expired'

    return (
        <div className="create-invite-link">
            <div className="create-invite-link__icon">
                <InviteSVGIcon />
            </div>
            <SpecialInput
                className="special-input w-[375px]"
                title={generateLinkData.title}
                channelName={generateLinkData.channelName}
                channelId={activeChannel.channel_id}
                value={generateLinkData.linkName}
                onChange={setLinkName}
                onChannelNameChange={setChannelName}
                hasError={!!errors.linkName}
            />
            <Input
                className="w-[200px]"
                placeholder="Укажите бюджет, если есть"
                value={generateLinkData.budget || ''}
                onChange={handleBudgetChange}
            />
            <Button
                onClick={generateLinkHandler}
                size="fit"
                disabled={!isValid}
                disabledBySubscriptionStatus={forbidToCreateGroups}
                className={clsx(`create-invite-link__button max-w-[166px]`, {
                    'create-invite-link__button--disabled':
                        !isValid || forbidToCreateGroups,
                })}
                wrapperClassName="create-invite-link__button-container"
                tooltipText="Чтобы создавать новые ссылки, оплатите подписку на сервис"
            >
                {isCreating ? (
                    <LoadingIndicator
                        style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '0.5em',
                        }}
                    />
                ) : (
                    <PlusSVGIcon
                        className={clsx(`button__icon-left`, {
                            'button__icon--disabled':
                                !isValid || forbidToCreateGroups,
                        })}
                    />
                )}
                Создать ссылку
            </Button>
        </div>
    )
}
